import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import { useDispatch } from "react-redux";
import confetti from "canvas-confetti";
import { GET_IMAGE } from "../../helpers/images";
import { ADD_REACT } from "../../api/react";
import { getJob } from "../../redux/jobs/actions";
import { UseUserDetails } from "../../hooks/auth";
import { UseCompanyColors } from "../../hooks/company";
import { REACTION_ICON_TYPE, REACTION_PROPERTY_TYPE } from "../../constants/reactions";
import socket from "../../configs/socket";
import socketConstants from "../../constants/socket";

const { SOCKET_EVENT } = socketConstants;

/* =========================================================
   Theme tokens (kept local so the rail/strip can drop into
   any feed without dragging a global theme along)
   ========================================================= */
// Design tokens — primary blue matches the rest of the feed (FeedCard.jsx TOKENS.primary)
const T = {
  ink: "#0F172A",
  muted: "#64748B",
  faint: "#94A3B8",
  line: "#E2E8F0",
  hairline: "#EEF2F6",
  surface: "#FFFFFF",
  surface2: "#F8FAFB",
  primary: "#2563EB",
  primary2: "#1D4ED8",
  primarySoft: "#DBEAFE",
};

// Modal radius — matches design system (FeedPage settings modal uses 10px)
const MODAL_RADIUS = 10;

const FONT = '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif';

/* =========================================================
   Suggestion derivation
   Pure client-side: ranks the loaded jobs and assigns a
   "why" tag. Replace with a backend endpoint later if needed.
   ========================================================= */
export function useKudoSuggestions(jobs, currentUserId, max = 8) {
  return useMemo(() => {
    if (!Array.isArray(jobs) || !jobs.length) return [];

    // Skip jobs the current user has already clapped
    const notYetClapped = jobs.filter((job) => {
      if (!currentUserId) return true;
      const reacts = job.reacts || [];
      return !reacts.some((r) => {
        const u = r.users?.[0];
        return u && String(u._id) === String(currentUserId);
      });
    });

    // Skip jobs the current user posted (don't clap yourself)
    const visible = notYetClapped.filter(
      (job) => String(job.userId) !== String(currentUserId),
    );

    // Compute a few scoreable signals
    const totals = visible.map((j) => j.amount ?? j.metadata?.total ?? 0);
    const maxTotal = Math.max(0, ...totals);

    const tagged = visible.map((job) => {
      const total = job.amount ?? job.metadata?.total ?? 0;
      const upsell = job.metadata?.upsell || 0;
      const isBigTicket = total >= 1000 || (maxTotal > 0 && total >= maxTotal * 0.9);
      const isUpsell = upsell > 0;
      const hasReview = !!job.metadata?.review;

      let why = { emoji: "⭐", label: "Recent win" };
      if (isBigTicket) why = { emoji: "💎", label: "Big ticket" };
      else if (isUpsell) why = { emoji: "📈", label: "Up-sell" };
      else if (hasReview) why = { emoji: "🔥", label: "Got a review" };
      else if (total > 0) why = { emoji: "⚡", label: "Job done" };

      return {
        jobId: job._id,
        userId: job.userId,
        firstName: job.user?.firstName || "Teammate",
        lastName: job.user?.lastName || "",
        avatarSrc: job.user?.image ? GET_IMAGE(job.user.image, "avatar") : null,
        initial: (job.user?.firstName || "T").charAt(0).toUpperCase(),
        jobTitle: job.metadata?.title || job.metadata?.description || "Job completed",
        total,
        why,
        // keep raw refs for the sheet
        _job: job,
      };
    });

    // Sort: big-ticket first, then most recent
    tagged.sort((a, b) => (b.total || 0) - (a.total || 0));

    // De-duplicate "why" labels — if the same tag would repeat, rotate
    // through synonyms so the strip reads as varied curation, not padding.
    const LABEL_VARIANTS = {
      "Big ticket": ["Big ticket", "Top earner", "Big win"],
      "Up-sell": ["Up-sell", "Up-seller", "Extra mile"],
      "Got a review": ["Got a review", "Loved by client", "Glowing review"],
      "Job done": ["Job done", "Steady hand", "Knocked it out"],
      "Recent win": ["Recent win", "Latest win", "Fresh win"],
    };
    const seenCounts = {};
    tagged.forEach((s) => {
      const baseTag = s.why.label;
      const variations = LABEL_VARIANTS[baseTag] || [baseTag];
      const seen = seenCounts[baseTag] || 0;
      // eslint-disable-next-line no-param-reassign
      s.why = { ...s.why, label: variations[Math.min(seen, variations.length - 1)] };
      seenCounts[baseTag] = seen + 1;
    });

    return tagged.slice(0, max);
  }, [jobs, currentUserId, max]);
}

/* =========================================================
   Clap action — same backend as <SlowClap/>
   ========================================================= */
function useClapAction() {
  const dispatch = useDispatch();
  const currentUser = UseUserDetails();
  return useCallback(
    async (jobId) => {
      if (!currentUser?._id || !jobId) return;
      socket.emit(SOCKET_EVENT.POST_SLOW_CLAP, { sender: currentUser._id, jobId });
      try {
        await ADD_REACT({
          propertyId: jobId,
          data: {
            userId: currentUser._id,
            propertyModel: REACTION_PROPERTY_TYPE.JOB,
            iconType: REACTION_ICON_TYPE.SLOW_CLAP,
          },
        });
      } catch (e) {
        // Swallow — the optimistic UI already handled removal; toast handled upstream.
      }
      socket.emit(SOCKET_EVENT.REFRESH_JOB, { jobId });
      dispatch(getJob(jobId));
    },
    [currentUser?._id, dispatch],
  );
}

/* =========================================================
   Avatar with green ring
   ========================================================= */
function RingAvatar({ src, initial, size = 56, ringWidth = 2 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        padding: `${ringWidth}px`,
        background: `conic-gradient(from 220deg, ${T.primary}, #60A5FA, ${T.primary})`,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          background: src ? `url(${src}) center/cover` : "#E2E8F0",
          color: "#475569",
          display: "grid",
          placeItems: "center",
          fontWeight: 700,
          fontSize: size * 0.32,
          border: `${Math.max(2, ringWidth)}px solid #fff`,
        }}
      >
        {!src && initial}
      </Box>
    </Box>
  );
}

/* =========================================================
   Clap confetti — canvas-based via `react-canvas-confetti`.

   Uses the existing dependency (already in package.json — also used by
   `components/ParticleEffects/ConfettiFireworks`). Renders ALL particles
   to a single full-viewport <canvas>, GPU-accelerated, ~6KB lib.

   - One DOM node, one paint region, one compositor layer.
   - Particles are computed in JS as plain numbers; canvas draws primitives
     directly to GPU memory. Zero React re-renders during the burst.

   Renders once at FeedPage scope. Buttons fire it via `fireClapConfetti`
   passing viewport coordinates.
   ========================================================= */
const CONFETTI_COLORS = ["#2563EB", "#60A5FA", "#F59E0B", "#EF4444", "#10B981", "#A855F7", "#EC4899"];

// Match the working ConfettiFireworks pattern exactly (top/left + width/height).
// `inset: 0` was confusing the canvas sizing on mobile and the canvas was rendering
// with zero drawing surface — particles fired but had nowhere visible to draw.
const confettiCanvasStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  zIndex: 9999, // above the modal sheet (1300) and any other UI
};

// Module-level ref so any component can fire from the same canvas without prop drilling.
let confettiInstance = null;

export function ClapConfettiCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return undefined;
    // Create a confetti instance bound to our canvas. resize:true so the
    // canvas drawing surface tracks the viewport. useWorker:false avoids the
    // OffscreenCanvas worker path which silently fails on some mobile browsers.
    const instance = confetti.create(canvasRef.current, {
      resize: true,
      useWorker: false,
    });
    confettiInstance = instance;
    return () => {
      // Clear so we don't try to fire into a torn-down canvas.
      if (confettiInstance === instance) confettiInstance = null;
      try { instance.reset(); } catch (e) { /* ignore */ }
    };
  }, []);

  return <canvas ref={canvasRef} style={confettiCanvasStyle} />;
}

function fireClapConfetti(originX, originY) {
  if (!confettiInstance || typeof window === "undefined") return;
  // Convert viewport pixels → normalized 0..1 origin (canvas is full-viewport)
  const x = originX / window.innerWidth;
  const y = originY / window.innerHeight;
  // Two quick bursts at slightly different angles for a richer pop.
  confettiInstance({
    particleCount: 60,
    spread: 70,
    startVelocity: 38,
    ticks: 90,
    decay: 0.9,
    gravity: 1,
    origin: { x, y },
    colors: CONFETTI_COLORS,
    scalar: 0.9,
    disableForReducedMotion: false,
  });
  setTimeout(() => {
    if (!confettiInstance) return;
    confettiInstance({
      particleCount: 30,
      spread: 110,
      startVelocity: 28,
      ticks: 80,
      decay: 0.92,
      gravity: 1,
      origin: { x, y },
      colors: CONFETTI_COLORS,
      scalar: 0.8,
      disableForReducedMotion: false,
    });
  }, 120);
}

/* =========================================================
   Detail body — shared by mobile sheet + desktop popover
   ========================================================= */
function KudoBody({ suggestion, onClap, onClose, onClapped }) {
  const [bursting, setBursting] = useState(false);

  const handleClap = (e) => {
    if (bursting) return;
    setBursting(true);
    // Fire confetti from the button's center on the canvas (single GPU layer).
    const btn = e?.currentTarget;
    if (btn && btn.getBoundingClientRect) {
      const rect = btn.getBoundingClientRect();
      fireClapConfetti(rect.left + rect.width / 2, rect.top + rect.height / 2);
    }
    onClap(suggestion.jobId);
    // Close shortly after the burst kicks off; the canvas keeps drawing on
    // top via z-index 1400 even after the modal unmounts, so the user sees
    // the full animation play out against the feed.
    setTimeout(() => {
      onClapped?.(suggestion.jobId);
      onClose?.();
    }, 320);
  };

  const { total } = suggestion;
  const fullName = `${suggestion.firstName} ${suggestion.lastName}`.trim();

  return (
    <Box sx={{ fontFamily: FONT, color: T.ink }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          gap: "14px",
          alignItems: "center",
          padding: "0 22px 14px",
          borderBottom: `1px solid ${T.hairline}`,
        }}
      >
        <RingAvatar src={suggestion.avatarSrc} initial={suggestion.initial} size={56} ringWidth={3} />
        <Box>
          <Box sx={{ fontSize: "17px", fontWeight: 700 }}>{fullName}</Box>
          <Box sx={{ fontSize: "12px", color: T.muted, marginTop: "2px" }}>{suggestion.jobTitle}</Box>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ padding: "16px 22px 6px" }}>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 12px",
            borderRadius: "999px",
            background: T.primarySoft,
            color: T.primary2,
            fontSize: "11px",
            fontWeight: 800,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {suggestion.why.emoji} {suggestion.why.label}
        </Box>
        <Box sx={{ fontSize: "14px", marginTop: "14px", lineHeight: 1.5 }}>
          {fullName} just wrapped <strong>{suggestion.jobTitle}</strong>
          {total > 0 ? <> for <strong>${total.toLocaleString()}</strong></> : null}.
          Drop a clap to tell them you noticed.
        </Box>
      </Box>

      {/* CTAs */}
      <Box sx={{ display: "flex", gap: "10px", padding: "16px 22px 18px" }}>
        <Box
          component="button"
          onClick={handleClap}
          disabled={bursting}
          sx={{
            position: "relative",
            flex: 1,
            height: "46px",
            borderRadius: "12px",
            border: 0,
            cursor: bursting ? "default" : "pointer",
            color: "#fff",
            fontFamily: FONT,
            fontSize: "14px",
            fontWeight: 700,
            background: `linear-gradient(180deg, ${T.primary} 0%, ${T.primary2} 100%)`,
            boxShadow: `0 8px 18px -8px rgba(22,163,74,.6)`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            overflow: "visible",
            transform: bursting ? "scale(0.97)" : "scale(1)",
            transition: "transform 120ms ease",
          }}
        >
          <Box component="span" sx={{ fontSize: "16px" }}>👏</Box>
          {bursting ? "Clapped!" : "Clap"}
        </Box>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            flex: 1,
            height: "46px",
            borderRadius: "12px",
            border: 0,
            background: "#F1F5F9",
            color: T.ink,
            fontFamily: FONT,
            fontSize: "14px",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Close
        </Box>
      </Box>
    </Box>
  );
}

/* =========================================================
   Tile — used in both rail (vertical list) and strip (horizontal carousel)
   ========================================================= */
function KudoTile({ s, onTap, layout = "strip", onDark = false }) {
  const isStrip = layout === "strip";
  return (
    <Box
      component="button"
      onClick={() => onTap(s)}
      sx={{
        background: "transparent",
        border: 0,
        cursor: "pointer",
        flex: isStrip ? "0 0 80px" : "0 0 auto",
        scrollSnapAlign: isStrip ? "start" : undefined,
        textAlign: "center",
        padding: 0,
        display: isStrip ? "block" : "flex",
        gap: "12px",
        alignItems: isStrip ? undefined : "center",
        width: isStrip ? "80px" : "100%",
        fontFamily: FONT,
        userSelect: "none",
      }}
    >
      <Box sx={{ position: "relative", width: 48, height: 48, margin: isStrip ? "0 auto" : 0 }}>
        <RingAvatar src={s.avatarSrc} initial={s.initial} size={48} ringWidth={2} />
        <Box
          sx={{
            position: "absolute",
            bottom: -2,
            right: -2,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#fff",
            border: "2px solid #fff",
            boxShadow: "0 2px 6px rgba(15,23,42,.18)",
            display: "grid",
            placeItems: "center",
            fontSize: 10,
          }}
        >
          {s.why.emoji}
        </Box>
      </Box>
      <Box sx={{ flex: isStrip ? undefined : 1, minWidth: 0, textAlign: isStrip ? "center" : "left" }}>
        <Box
          sx={{
            marginTop: isStrip ? "6px" : 0,
            fontSize: isStrip ? "11px" : "13px",
            fontWeight: 600,
            color: onDark ? "#fff" : T.ink,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {s.firstName}
        </Box>
        <Box
          sx={{
            fontSize: isStrip ? "10px" : "11px",
            // Lighter blue on dark bg for contrast; brand blue on light bg
            color: onDark ? "#A5B4FC" : T.primary,
            fontWeight: 500,
            letterSpacing: 0,
            marginTop: "2px",
          }}
        >
          {s.why.label}
        </Box>
      </Box>
    </Box>
  );
}

/* =========================================================
   Mobile bottom sheet
   ========================================================= */
function MobileSheet({ open, suggestion, onClose, onClap, onClapped }) {
  if (!open || !suggestion) return null;
  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 1300 }}>
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          background: "rgba(15,23,42,.5)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          background: "#fff",
          borderTopLeftRadius: `${MODAL_RADIUS}px`,
          borderTopRightRadius: `${MODAL_RADIUS}px`,
          boxShadow: "0 -10px 30px rgba(15,23,42,.18)",
          padding: "8px 0 18px",
          fontFamily: FONT,
          animation: "kudoSlide .26s ease-out",
        }}
      >
        <style>{`@keyframes kudoSlide { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
        <Box sx={{ width: 44, height: 4, background: "#CBD5E1", borderRadius: 999, margin: "4px auto 14px" }} />
        <KudoBody suggestion={suggestion} onClap={onClap} onClose={onClose} onClapped={onClapped} />
      </Box>
    </Box>
  );
}

/* =========================================================
   Desktop popover (anchor-less, simple modal-style for now)
   ========================================================= */
function DesktopPopover({ open, suggestion, onClose, onClap, onClapped }) {
  if (!open || !suggestion) return null;
  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 1300, display: "grid", placeItems: "center" }}>
      <Box
        onClick={onClose}
        sx={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.45)" }}
      />
      <Box
        sx={{
          position: "relative",
          width: 360,
          background: "#fff",
          borderRadius: `${MODAL_RADIUS}px`,
          border: `1px solid ${T.line}`,
          boxShadow: "0 24px 60px -20px rgba(15,23,42,.35), 0 8px 20px rgba(15,23,42,.08)",
          paddingTop: "20px",
          fontFamily: FONT,
          animation: "kudoZoom .2s ease-out",
        }}
      >
        <style>{`@keyframes kudoZoom { from { transform: scale(.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <KudoBody suggestion={suggestion} onClap={onClap} onClose={onClose} onClapped={onClapped} />
      </Box>
    </Box>
  );
}

/* =========================================================
   Public components
   ========================================================= */
export function KudosStrip({ suggestions, onClap }) {
  const [active, setActive] = useState(null);
  const [hidden, setHidden] = useState(new Set());
  const [dismissed, setDismissed] = useState(false);
  const visible = suggestions.filter((s) => !hidden.has(s.jobId));
  // Use the company's brand primary color (same source the Sidebar uses).
  // Fallback matches the Sidebar's fallback so the surfaces stay paired.
  const { primaryColor } = UseCompanyColors();
  const brandBg = primaryColor || "#1E1B4B";

  if (dismissed) return null;

  if (!visible.length) return null;

  return (
    <>
      <Box
        sx={{
          margin: "0 0 8px",
          padding: "10px 0 8px",
          // Brand color from UseCompanyColors().primaryColor — same source the Sidebar uses
          background: brandBg,
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "10px",
          fontFamily: FONT,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            padding: "0 12px 8px",
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: 700, color: "#fff" }}>Give kudos</Box>
          <Box
            component="button"
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss kudos suggestions"
            sx={{
              display: "grid",
              placeItems: "center",
              width: "26px",
              height: "26px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.85)",
              cursor: "pointer",
              padding: 0,
              transition: "background 120ms ease",
              "&:hover": { background: "rgba(255,255,255,0.16)" },
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </Box>
        </Box>
        {/* Carousel wrapper with right-edge fade so users see content is scrollable */}
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              padding: "2px 12px 4px",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              msOverflowStyle: "none",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {visible.map((s) => (
              <KudoTile key={s.jobId} s={s} onTap={setActive} layout="strip" onDark />
            ))}
          </Box>
          {/* Fade hint on the right edge — only shows when there's something to scroll to */}
          {visible.length > 4 && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                right: 0,
                width: "32px",
                background: `linear-gradient(90deg, rgba(0,0,0,0) 0%, ${brandBg} 70%)`,
                pointerEvents: "none",
                borderTopRightRadius: "10px",
                borderBottomRightRadius: "10px",
              }}
            />
          )}
        </Box>
      </Box>
      <MobileSheet
        open={!!active}
        suggestion={active}
        onClose={() => setActive(null)}
        onClap={onClap}
        onClapped={(jobId) => setHidden((prev) => new Set(prev).add(jobId))}
      />
    </>
  );
}

/**
 * useStickyOnScroll — JS-driven sticky positioning.
 *
 * CSS `position: sticky` is unreliable in this app's layout because an
 * ancestor (Main.jsx Box) has `overflow-x-hidden overflow-y-visible` which
 * computes to `overflow: hidden auto` per CSS spec. That establishes a
 * scroll container that doesn't actually scroll (no fixed height), and
 * sticky descendants stick relative to it instead of the window — making
 * them appear static.
 *
 * This hook tracks scroll on whatever element is actually scrolling
 * (Main.jsx Box AND/OR window), reads the placeholder's bounding rect, and
 * toggles between in-flow and fixed positioning so the rail visually pins
 * below the TopBar regardless of which ancestor is the real scroll root.
 */
function useStickyOnScroll(topOffset = 64) {
  const placeholderRef = useRef(null);
  const [pinned, setPinned] = useState(false);
  const [layout, setLayout] = useState({ left: 0, width: 320 });

  useEffect(() => {
    const update = () => {
      const el = placeholderRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Pin when the placeholder's top edge has scrolled above the offset
      const shouldPin = rect.top <= topOffset;
      setPinned(shouldPin);
      // Always keep the layout values fresh so resize/sidebar-toggle is handled
      setLayout({ left: rect.left, width: rect.width });
    };

    update();

    // Find every scrollable ancestor — any of them could be moving content
    const scrollers = [window];
    let node = placeholderRef.current?.parentElement;
    while (node) {
      const cs = window.getComputedStyle(node);
      const oy = cs.overflowY;
      const ox = cs.overflowX;
      if (oy === "auto" || oy === "scroll" || ox === "auto" || ox === "scroll") {
        scrollers.push(node);
      }
      node = node.parentElement;
    }

    scrollers.forEach((s) => s.addEventListener("scroll", update, { passive: true }));
    window.addEventListener("resize", update);

    return () => {
      scrollers.forEach((s) => s.removeEventListener("scroll", update));
      window.removeEventListener("resize", update);
    };
  }, [topOffset]);

  return { placeholderRef, pinned, layout };
}

export function KudosRail({ suggestions, onClap }) {
  const [active, setActive] = useState(null);
  const [hidden, setHidden] = useState(new Set());
  const [dismissed, setDismissed] = useState(false);
  const visible = suggestions.filter((s) => !hidden.has(s.jobId));
  const TOP_OFFSET = 72;
  const { placeholderRef, pinned, layout } = useStickyOnScroll(TOP_OFFSET);

  if (!visible.length || dismissed) return null;

  // Approximate height of the rail body so the placeholder reserves space when
  // the rail switches to fixed (otherwise the layout would jump).
  const RAIL_APPROX_HEIGHT = 60 + visible.slice(0, 5).length * 70 + 40; // header + items + footer

  return (
    <>
      {/* Placeholder — always in flow. Reserves the column width and acts as
          the anchor we measure to decide when to pin. */}
      <Box
        ref={placeholderRef}
        sx={{
          width: "100%",
          minHeight: pinned ? `${RAIL_APPROX_HEIGHT}px` : 0,
        }}
      />
      <Box
        sx={{
          background: T.surface,
          border: `1px solid ${T.line}`,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow:
            "0 1px 2px rgba(15,23,42,0.04), 0 18px 36px rgba(15,23,42,0.05)",
          fontFamily: FONT,
          // JS-driven sticky: in-flow until scrolled past, then pinned via fixed
          position: pinned ? "fixed" : "static",
          top: pinned ? `${TOP_OFFSET}px` : "auto",
          left: pinned ? `${layout.left}px` : "auto",
          width: pinned ? `${layout.width}px` : "100%",
          zIndex: pinned ? 5 : "auto",
          maxHeight: "calc(100vh - 96px)",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            padding: "14px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${T.hairline}`,
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: 700, color: T.ink }}>Give kudos</Box>
          <Box
            component="button"
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss kudos suggestions"
            sx={{
              display: "grid",
              placeItems: "center",
              width: "26px",
              height: "26px",
              borderRadius: "50%",
              background: T.surface2,
              border: `1px solid ${T.hairline}`,
              color: T.muted,
              cursor: "pointer",
              padding: 0,
              transition: "background 120ms ease, color 120ms ease",
              "&:hover": { background: T.line, color: T.ink },
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </Box>
        </Box>

        <Box>
          {visible.slice(0, 5).map((s, i) => (
            <Box
              key={s.jobId}
              sx={{
                padding: "12px 18px",
                borderTop: i === 0 ? "none" : `1px solid ${T.hairline}`,
              }}
            >
              <KudoTile s={s} onTap={setActive} layout="row" />
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            padding: "10px 18px",
            background: T.surface2,
            fontSize: "11px",
            color: T.faint,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: `1px solid ${T.hairline}`,
          }}
        >
          <span>Tap a teammate to clap</span>
        </Box>
      </Box>
      <DesktopPopover
        open={!!active}
        suggestion={active}
        onClose={() => setActive(null)}
        onClap={onClap}
        onClapped={(jobId) => setHidden((prev) => new Set(prev).add(jobId))}
      />
    </>
  );
}

/* =========================================================
   Combined hook used by the page — derives + binds clap action
   ========================================================= */
export function useKudosController(jobs) {
  const currentUser = UseUserDetails();
  const suggestions = useKudoSuggestions(jobs, currentUser?._id);
  const onClap = useClapAction();
  return { suggestions, onClap };
}
